import { useNavigate } from 'react-router-dom';
import { useNotificacoes } from './useNotificacoes';

let redirecionando = false;
let ultimaMensagemErro = '';
let ultimaMensagemTimestamp = 0;

export const useHttpClient = () => {
    const navigate = useNavigate();
    const { exibirErro, exibirAlerta, exibirSucesso } = useNotificacoes();

    const exibirMensagemErroUnica = mensagem => {
        const agora = Date.now();
        if (
            mensagem !== ultimaMensagemErro ||
            agora - ultimaMensagemTimestamp > 3000
        ) {
            exibirErro(mensagem);
            ultimaMensagemErro = mensagem;
            ultimaMensagemTimestamp = agora;
        }
    };

    const tratarErroDeRede = error => {
        if (error.message === 'Failed to fetch') {
            exibirMensagemErroUnica('Não foi possível conectar ao servidor.');
            localStorage.removeItem('token');
            if (!redirecionando) {
                redirecionando = true;
                setTimeout(() => {
                    navigate('/login');
                    redirecionando = false;
                }, 100);
            }
            return true;
        }
        return false;
    };

    const getToken = () => localStorage.getItem('token');
    const getHeaders = () => {
        const token = getToken();
        return {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
        };
    };

    const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

    // Função genérica
    async function request(method, url, body = null, options = {}) {
        const { responseType, fullResponse } = options;
        const headers = {
            ...getHeaders(),
            ...(options.headers || {})
        };
        // Se for blob, aceitamos qualquer tipo
        if (responseType === 'blob') {
            headers['Accept'] = 'application/pdf,*/*';
        }

        const resp = await fetch(`${BASE_URL}${url}`, {
            method,
            headers,
            body: body != null && headers['Content-Type']
                ? JSON.stringify(body)
                : body,
        });

        // Rede
        if (!resp.ok && resp.status === 0) {
            throw new Error('Failed to fetch');
        }

        // 401/403
        if (resp.status === 401 || resp.status === 403) {
            exibirMensagemErroUnica('Sessão expirada. Faça login novamente.');
            localStorage.removeItem('token');
            if (!redirecionando) {
                redirecionando = true;
                setTimeout(() => {
                    navigate('/login');
                    redirecionando = false;
                }, 100);
            }
            throw new Error('Sessão expirada');
        }

        // Se quiser a resposta completa
        if (fullResponse) {
            return resp;
        }

        // corpo
        if (responseType === 'blob') {
            const blob = await resp.blob();
            if (!resp.ok) {
                // tenta texto
                const txt = await blob.text();
                throw new Error(txt || `Erro ${resp.status}`);
            }
            return blob;
        } else {
            const contentType = resp.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');
            const data = isJson
                ? await resp.json()
                : await resp.text();

            if (!resp.ok) {
                const msg =
                    isJson && data?.message
                        ? data.message
                        : typeof data === 'string'
                            ? data
                            : 'Erro desconhecido';
                exibirMensagemErroUnica(msg);
                throw new Error(msg);
            }

            // notifica se vier tipoRetorno
            if (isJson && data?.tipoRetorno) {
                const msg = data.dsMensagem || '';
                if (data.tipoRetorno === 'SUCESSO') exibirSucesso(msg);
                if (data.tipoRetorno === 'ALERTA') exibirAlerta(msg);
                if (data.tipoRetorno === 'ERRO') exibirErro(msg);
            }

            return data;
        }
    }

    const get = (url, options) =>
        request('GET', url, null, options).catch(err => {
            if (!tratarErroDeRede(err)) throw err;
        });

    const post = (url, body, options) =>
        request('POST', url, body, options).catch(err => {
            if (!tratarErroDeRede(err)) throw err;
        });

    const put = (url, body, options) =>
        request('PUT', url, body, options).catch(err => {
            if (!tratarErroDeRede(err)) throw err;
        });

    const del = (url, options) =>
        request('DELETE', url, null, options).catch(err => {
            if (!tratarErroDeRede(err)) throw err;
        });

    // Novo método: upload de FormData sem setar Content-Type
    const upload = async (url, formData, options = {}) => {
        const { responseType, fullResponse } = options;
        const token = getToken();

        const headers = {
            ...(responseType === 'blob' ? { 'Accept': 'application/pdf,*/*' } : {}),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            // NÃO definir 'Content-Type' aqui! O browser cuidará do boundary.
        };

        let resp;
        try {
            resp = await fetch(`${BASE_URL}${url}`, {
                method: 'POST',
                headers,
                body: formData,
            });
        } catch (e) {
            // Mantém padrão do hook
            throw new Error('Failed to fetch');
        }

        if (!resp.ok && resp.status === 0) {
            throw new Error('Failed to fetch');
        }

        // Mesma política de sessão expirada do hook
        if (resp.status === 401 || resp.status === 403) {
            exibirMensagemErroUnica('Sessão expirada. Faça login novamente.');
            localStorage.removeItem('token');
            if (!redirecionando) {
                redirecionando = true;
                setTimeout(() => {
                    navigate('/login');
                    redirecionando = false;
                }, 100);
            }
            throw new Error('Sessão expirada');
        }

        if (fullResponse) return resp;

        if (responseType === 'blob') {
            const blob = await resp.blob();
            if (!resp.ok) {
                const txt = await blob.text();
                throw new Error(txt || `Erro ${resp.status}`);
            }
            return blob;
        }

        const contentType = resp.headers.get('content-type') || '';
        const isJson = contentType.includes('application/json');
        const data = isJson ? await resp.json() : await resp.text();

        if (!resp.ok) {
            const msg = isJson && data?.message
                ? data.message
                : (typeof data === 'string' ? data : 'Erro desconhecido');
            exibirMensagemErroUnica(msg);
            throw new Error(msg);
        }

        // Mantém os toasts automáticos do hook
        if (isJson && data?.tipoRetorno) {
            const msg = data.dsMensagem || '';
            if (data.tipoRetorno === 'SUCESSO') exibirSucesso(msg);
            if (data.tipoRetorno === 'ALERTA') exibirAlerta(msg);
            if (data.tipoRetorno === 'ERRO') exibirErro(msg);
        }

        return data;
    };


    return { get, post, put, del, upload };
};
